// The request form as a sheet on a phone (shell/demo.html). The form sits at the
// end of a long page; on a narrow screen a bar pinned to the bottom offers it
// wherever the visitor is, and every "Try it free" on the page opens it in place
// instead of scrolling thousands of pixels down. The form and its "done" panel
// move into the sheet and back: one form, one set of ids for main.ts.

/** The width the sheet works at - the phone layout's (landing.css). */
export const SHEET_MEDIA = '(max-width: 680px)';

/** Whether the pinned bar shows: on a phone, with the page's own call to try
 *  it and the form itself both out of sight, and no cookie bar waiting for an
 *  answer (the bar asks first, and the two would stack). */
export function stickyShown(s: { phone: boolean; heroInView: boolean; formInView: boolean; barUp: boolean; sheetOpen: boolean }): boolean {
  return s.phone && !s.heroInView && !s.formInView && !s.barUp && !s.sheetOpen;
}

export function startSheet(): void {
  const form = document.querySelector<HTMLFormElement>('#demo-form');
  const done = document.querySelector<HTMLElement>('#demo-done');
  const sticky = document.querySelector<HTMLElement>('#lp-sticky');
  const openBtn = document.querySelector<HTMLButtonElement>('#lp-sticky-open');
  const sheet = document.querySelector<HTMLDialogElement>('#lp-sheet');
  const sheetBody = document.querySelector<HTMLElement>('#lp-sheet-body');
  const closeBtn = document.querySelector<HTMLButtonElement>('#lp-sheet-close');
  if (!form || !done || !sticky || !openBtn || !sheet || !sheetBody || !closeBtn || typeof sheet.showModal !== 'function') return;

  const phone = window.matchMedia(SHEET_MEDIA);
  // Where the form lives on the page, kept so it goes back exactly there.
  const home = document.createComment('demo-form');
  form.before(home);
  const section = form.closest<HTMLElement>('section') ?? form;
  const hero = document.querySelector<HTMLElement>('main a[href$="#demo"]');
  const bars = [...document.querySelectorAll<HTMLElement>('#cookiebar, #consentbar')];
  const state = { heroInView: true, formInView: false };

  const update = () => {
    sticky.hidden = !stickyShown({
      phone: phone.matches,
      heroInView: state.heroInView,
      formInView: state.formInView,
      barUp: bars.some((b) => !b.hidden),
      sheetOpen: sheet.open,
    });
    // The page keeps room under its end while the bar is up, so the footer's
    // last line - the language switcher - is never under it (landing.css).
    document.body.classList.toggle('lp-has-sticky', !sticky.hidden);
  };

  const watch = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (e.target === section) state.formInView = e.isIntersecting;
      else state.heroInView = e.isIntersecting;
    }
    update();
  });
  watch.observe(section);
  if (hero) watch.observe(hero);
  else state.heroInView = false;
  const barsChange = new MutationObserver(update);
  for (const b of bars) barsChange.observe(b, { attributes: true, attributeFilter: ['hidden'] });
  phone.addEventListener('change', update);

  const open = (from: HTMLElement | null) => {
    sheetBody.append(form, done);
    sheet.showModal();
    update();
    (form.querySelector<HTMLElement>('input[name="name"]') ?? closeBtn).focus({ preventScroll: true });
    sheet.addEventListener('close', () => from?.focus({ preventScroll: true }), { once: true });
  };
  sheet.addEventListener('close', () => {
    home.after(form, done);
    update();
  });
  closeBtn.addEventListener('click', () => sheet.close());
  // A tap on the dimmed page around the sheet closes it, as a sheet does.
  sheet.addEventListener('click', (e) => {
    if (e.target === sheet) sheet.close();
  });
  openBtn.addEventListener('click', () => open(openBtn));
  // Every "Try it free" on a phone opens the sheet where the visitor is.
  document.addEventListener('click', (e) => {
    if (!phone.matches || sheet.open) return;
    const link = (e.target as Element | null)?.closest<HTMLAnchorElement>('a[href$="#demo"]');
    if (!link || link.closest('#lp-sheet')) return;
    e.preventDefault();
    open(link);
  });
  update();
}
